'use client';

import { useState } from 'react';

import { useTranslations } from 'next-intl';

import {
    faBug,
    faCheckCircle,
    faCircleExclamation,
    faEyeSlash,
    faPaperPlane,
    faXmark,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    ActionIcon,
    Alert,
    Button,
    Flex,
    Modal,
    Stack,
    Text,
    Textarea,
    ThemeIcon,
} from '@mantine/core';

import { useAppContext } from '@context/AppContext';

const STORAGE_KEY = 'nli:errorReport:ignored';

function getErrorFingerprint(error: Error): string {
    const firstFrame = error.stack
        ?.split('\n')
        .map((l) => l.trim())
        .find((l) => l.startsWith('at ') || l.includes('@'));
    return `${error.message}|${firstFrame ?? ''}`;
}

function ignoreError(error: Error): void {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        const ignored: string[] = raw ? JSON.parse(raw) : [];
        const fingerprint = getErrorFingerprint(error);
        if (!ignored.includes(fingerprint)) {
            ignored.push(fingerprint);
            localStorage.setItem(STORAGE_KEY, JSON.stringify(ignored));
        }
    } catch {
        // localStorage indisponible (SSR, private browsing strict) → silencieux
    }
}

type SendStatus = 'idle' | 'sending' | 'success' | 'error';

export default function ErrorReportModal() {
    const t = useTranslations();
    const [errorReport, setErrorReport] = useAppContext('errorReport');
    const isOpen = errorReport?.show ?? false;
    const close = () => setErrorReport({ show: false, error: null, url: null });
    const errorInfo = errorReport;
    const [description, setDescription] = useState('');
    const [status, setStatus] = useState<SendStatus>('idle');

    const handleClose = () => {
        close();
    };

    const handleIgnoreForSession = () => {
        const error = errorInfo?.error;
        if (error instanceof Error) ignoreError(error);
        close();
    };

    const handleSend = async () => {
        setStatus('sending');
        try {
            const error = errorInfo?.error;
            const res = await fetch('/api/error-report', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    description: description.trim() || undefined,
                    message: error instanceof Error ? error.message : String(error ?? ''),
                    stack: error instanceof Error ? error.stack : undefined,
                    url: errorInfo?.url,
                }),
            });
            if (!res.ok) throw new Error(`Server responded with status ${res.status}`);
            setStatus('success');
            ignoreError(error); // Ne pas rouvrir la modal si cette erreur précise se reproduit après envoi sur cette session
        } catch {
            setStatus('error');
        }
    };

    return (
        <Modal
            opened={isOpen}
            onClose={handleClose}
            onExitTransitionEnd={() => {
                setDescription('');
                setStatus('idle');
            }}
            withCloseButton={false}
            centered
            size="lg"
            radius="lg"
            overlayProps={{ backgroundOpacity: 0.45, blur: 3 }}
            zIndex={999990}
            padding="xl"
        >
            {/* Close button */}
            <ActionIcon
                variant="subtle"
                color="gray"
                pos="absolute"
                top={12}
                right={12}
                onClick={handleClose}
                aria-label={t('close')}
            >
                <FontAwesomeIcon icon={faXmark} />
            </ActionIcon>

            {status === 'success' ? (
                /* ── État succès ── */
                <Flex direction="column" align="center" gap="md" py="md">
                    <ThemeIcon size={56} radius="xl" color="green" variant="light">
                        <FontAwesomeIcon icon={faCheckCircle} size="xl" />
                    </ThemeIcon>
                    <Text fw={700} size="lg" ta="center">
                        {t('report-sent-thank-you')}
                    </Text>
                    <Text size="sm" c="dimmed" ta="center" maw={380}>
                        {t('your-feedback-has-been-forwarded')}
                    </Text>
                    <Button mt="xs" radius="xl" onClick={handleClose}>
                        {t('close')}
                    </Button>
                </Flex>
            ) : (
                /* ── Formulaire ── */
                <Stack gap="lg">
                    {/* En-tête */}
                    <Flex align="center" gap="md">
                        <ThemeIcon size={48} radius="xl" color="red" variant="light">
                            <FontAwesomeIcon icon={faBug} size="lg" />
                        </ThemeIcon>
                        <Flex direction="column" gap={2}>
                            <Text fw={700} size="lg">
                                {t('an-unexpected-error-occurred')}
                            </Text>
                            <Text size="xs" c="dimmed">
                                {t('we-are-sorry-this-error-was-detected-automatically')}
                            </Text>
                        </Flex>
                    </Flex>

                    {/* Message principal */}
                    <Text size="sm" c="dimmed" style={{ lineHeight: 1.6 }}>
                        {t('our-technical-team-will-do-everything-to-resolve-this-issue')}
                    </Text>

                    {/* Champ description optionnel */}
                    <Textarea
                        label={t('what-were-you-doing-when-the-error-occurred')}
                        autosize
                        minRows={3}
                        maxRows={6}
                        radius="md"
                        value={description}
                        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setDescription(e.currentTarget.value.slice(0, 1000))}
                        disabled={status === 'sending'}
                        description={`${1000 - description.length} ${1000 - description.length !== 1 ? t('remaining-characters') : t('remaining-character')}`}
                        styles={{ description: { textAlign: 'right', color: description.length >= 900 ? (description.length >= 1000 ? 'var(--mantine-color-red-6)' : 'var(--mantine-color-orange-6)') : undefined } }}
                    />

                    {/* Alerte si l'envoi a échoué */}
                    {status === 'error' && (
                        <Alert
                            icon={<FontAwesomeIcon icon={faCircleExclamation} />}
                            color="red"
                            radius="md"
                            variant="light"
                        >
                            {t('failed-to-send-report')}
                        </Alert>
                    )}

                    {/* Actions */}
                    <Flex justify="flex-end" gap="sm">
                        <Button
                            variant="subtle"
                            color="gray"
                            radius="xl"
                            onClick={handleClose}
                            disabled={status === 'sending'}
                        >
                            {t('ignore')}
                        </Button>
                        <Button
                            variant="subtle"
                            color="orange"
                            radius="xl"
                            leftSection={<FontAwesomeIcon icon={faEyeSlash} />}
                            onClick={handleIgnoreForSession}
                            disabled={status === 'sending'}
                        >
                            {t('ignore-errors-for-this-session')}
                        </Button>
                        <Button
                            radius="xl"
                            leftSection={<FontAwesomeIcon icon={faPaperPlane} />}
                            loading={status === 'sending'}
                            onClick={handleSend}
                        >
                            {t('send-error-report')}
                        </Button>
                    </Flex>
                </Stack>
            )}
        </Modal>
    );
}
